import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GruposService, Grupo } from 'src/app/services/admin/grupos/grupos.service';
import { Gtp } from 'src/app/shared/gtp';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-grupos',
  templateUrl: './grupos.component.html',
  styleUrls: ['./grupos.component.scss']
})
export class GruposComponent implements OnInit {

  loading = false;
  searchText = '';
  grupos: Grupo[] = [];
  grupoSeleccionado: Grupo | null = null;
  mostrando = 'lista'; // 'lista' o 'editar'

  // Para usuarios
  mostraFormularioUsuarios = false;
  searchUsuarios = '';
  usuariosFiltrados: any[] = [];
  usuariosCompletos: any[] = [];

  // Para vehículos
  mostraFormularioVehiculos = false;
  searchVehiculos = '';
  vehiculosFiltrados: any[] = [];
  vehiculosCompletos: any[] = [];

  constructor(
    private gruposService: GruposService,
    private gtp: Gtp,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.cargarGrupos();
  }

  cargarGrupos(): void {
    this.loading = true;
    this.gruposService.getGrupos(this.searchText).subscribe(
      (response: any) => {
        this.grupos = response.data || [];
        this.loading = false;
      },
      (error) => {
        console.error('Error cargando grupos:', error);
        Swal.fire('Error', 'No se pudieron cargar los grupos', 'error');
        this.loading = false;
      }
    );
  }

  onSearchChange(value: string): void {
    this.searchText = value;
    this.cargarGrupos();
  }

  crearGrupo(): void {
    this.grupoSeleccionado = {
      des_grupo_cliente: '',
      desde: '08:00:00',
      hasta: '18:00:00',
      lun: true,
      mar: true,
      mie: true,
      jue: true,
      vie: true,
      sab: false,
      dom: false,
      usuarios: [],
      vehiculos: []
    };
    this.mostrando = 'editar';
  }

  editarGrupo(grupo: Grupo): void {
    this.grupoSeleccionado = { ...grupo };
    this.mostrando = 'editar';

    // Cargar usuarios y vehículos si el grupo ya existe
    if (grupo.cod_grupo_cliente) {
      this.cargarUsuariosYVehiculos(grupo.cod_grupo_cliente);
    }
  }

  cargarUsuariosYVehiculos(grupoId: number): void {
    // Cargar usuarios del grupo
    this.gruposService.getUsuariosDisponibles(grupoId).subscribe(
      (response: any) => {
        const usuarios = response.data || [];
        if (this.grupoSeleccionado) {
          this.grupoSeleccionado.usuarios = usuarios.filter((u: any) => u.isAssigned);
        }
      },
      (error) => console.error('Error cargando usuarios:', error)
    );

    // Cargar vehículos del grupo
    this.gruposService.getVehiculosDisponibles(grupoId).subscribe(
      (response: any) => {
        const vehiculos = response.data || [];
        if (this.grupoSeleccionado) {
          this.grupoSeleccionado.vehiculos = vehiculos.filter((v: any) => v.isAssigned);
        }
      },
      (error) => console.error('Error cargando vehículos:', error)
    );
  }

  guardarGrupo(grupo: Grupo): void {
    if (grupo.cod_grupo_cliente) {
      this.gruposService.updateGrupo(grupo.cod_grupo_cliente, grupo).subscribe(
        (response: any) => {
          Swal.fire('Éxito', 'Grupo actualizado', 'success');
          this.cargarGrupos();
          this.mostrando = 'lista';
        },
        (error) => {
          Swal.fire('Error', 'No se pudo actualizar el grupo', 'error');
        }
      );
    } else {
      // Guardar referencia a usuarios y vehículos seleccionados antes de crear
      const usuariosPendientes = grupo.usuarios || [];
      const vehiculosPendientes = grupo.vehiculos || [];

      this.gruposService.createGrupo(grupo).subscribe(
        (response: any) => {
          const nuevoGrupoId = response.data?.cod_grupo_cliente;

          // Si hay usuarios o vehículos pre-seleccionados, guardarlos en el grupo recién creado
          if (nuevoGrupoId && (usuariosPendientes.length > 0 || vehiculosPendientes.length > 0)) {
            const promesas: Promise<any>[] = [];

            if (usuariosPendientes.length > 0) {
              const usuariosData = usuariosPendientes.map((u: any) => u.usuario);
              promesas.push(
                this.gruposService.saveUsuarios(nuevoGrupoId, { usuarios: usuariosData }).toPromise()
              );
            }

            if (vehiculosPendientes.length > 0) {
              const vehiculosData = vehiculosPendientes.map((v: any) => v.cod_equipo_rastreo);
              promesas.push(
                this.gruposService.saveVehiculos(nuevoGrupoId, { vehiculos: vehiculosData }).toPromise()
              );
            }

            Promise.all(promesas).then(() => {
              Swal.fire('Éxito', 'Grupo creado con usuarios y vehículos asignados', 'success');
              this.cargarGrupos();
              this.mostrando = 'lista';
            }).catch((err) => {
              console.error('Error guardando usuarios/vehículos del nuevo grupo:', err);
              Swal.fire('Advertencia', 'Grupo creado, pero hubo un error al asignar usuarios/vehículos', 'warning');
              this.cargarGrupos();
              this.mostrando = 'lista';
            });
          } else {
            Swal.fire('Éxito', 'Grupo creado', 'success');
            this.cargarGrupos();
            this.mostrando = 'lista';
          }
        },
        (error) => {
          Swal.fire('Error', 'No se pudo crear el grupo', 'error');
        }
      );
    }
  }

  cancelarEdicion(): void {
    this.grupoSeleccionado = null;
    this.mostrando = 'lista';
  }

  eliminarGrupo(grupo: Grupo): void {
    Swal.fire({
      title: '¿Eliminar grupo?',
      text: `¿Desea eliminar el grupo "${grupo.des_grupo_cliente}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.gruposService.deleteGrupo(grupo.cod_grupo_cliente).subscribe(
          (response: any) => {
            Swal.fire('Éxito', 'Grupo eliminado', 'success');
            this.cargarGrupos();
          },
          (error) => {
            Swal.fire('Error', 'No se pudo eliminar el grupo', 'error');
          }
        );
      }
    });
  }

  cerrar(): void {
    this.gtp.setAdminSidebar(true);
  }

  getHorario(grupo: Grupo): string {
    if (grupo.desde || grupo.hasta) {
      return `${grupo.desde || '00:00:00'} - ${grupo.hasta || '23:59:59'}`;
    }
    return 'Sin horario';
  }

  // Métodos para usuarios
  mostrarUsuarios(): void {
    this.cargarUsuariosDisponibles();
    this.mostraFormularioUsuarios = true;
  }

  cerrarFormularioUsuarios(): void {
    this.mostraFormularioUsuarios = false;
    this.searchUsuarios = '';
    this.usuariosFiltrados = [];
  }

  cargarUsuariosDisponibles(): void {
    const grupoId = this.grupoSeleccionado?.cod_grupo_cliente || 0;

    this.gruposService.getUsuariosDisponibles(grupoId).subscribe(
      (response: any) => {
        this.usuariosCompletos = (response.data || []).map((u: any) => ({
          ...u,
          checked: this.grupoSeleccionado?.usuarios?.some(uu => uu.usuario === u.usuario) || false
        }));
        this.usuariosFiltrados = [...this.usuariosCompletos];
      },
      (error) => {
        console.error('Error cargando usuarios:', error);
        Swal.fire('Error', 'No se pudieron cargar los usuarios', 'error');
      }
    );
  }

  filtrarUsuarios(): void {
    if (!this.searchUsuarios.trim()) {
      this.usuariosFiltrados = [...this.usuariosCompletos];
    } else {
      const search = this.searchUsuarios.toLowerCase();
      this.usuariosFiltrados = this.usuariosCompletos.filter(u =>
        u.nombre.toLowerCase().includes(search) || u.usuario.toLowerCase().includes(search)
      );
    }
  }

  guardarUsuarios(): void {
    const usuariosChecked = this.usuariosCompletos.filter(u => u.checked);
    const usuariosSeleccionados = usuariosChecked.map(u => u.usuario);

    // Si el grupo aún no existe, guardar localmente y cerrar
    if (!this.grupoSeleccionado?.cod_grupo_cliente) {
      this.grupoSeleccionado!.usuarios = usuariosChecked;
      this.grupoSeleccionado!.usuariosCount = usuariosChecked.length;
      Swal.fire('Info', 'Usuarios seleccionados. Se guardarán al crear el grupo.', 'info');
      this.cerrarFormularioUsuarios();
      return;
    }

    this.gruposService.saveUsuarios(this.grupoSeleccionado.cod_grupo_cliente, {
      usuarios: usuariosSeleccionados
    }).subscribe(
      (response: any) => {
        Swal.fire('Éxito', 'Usuarios asignados exitosamente', 'success');
        this.grupoSeleccionado!.usuarios = usuariosChecked;

        // Actualizar contador
        if (this.grupoSeleccionado) {
          this.grupoSeleccionado.usuariosCount = usuariosChecked.length;

          // Actualizar también en el array de grupos para reflejar el cambio en los cards
          const grupoEnLista = this.grupos.find(g => g.cod_grupo_cliente === this.grupoSeleccionado?.cod_grupo_cliente);
          if (grupoEnLista) {
            grupoEnLista.usuariosCount = usuariosChecked.length;
          }
        }

        this.cerrarFormularioUsuarios();
      },
      (error) => {
        console.error('Error guardando usuarios:', error);
        Swal.fire('Error', 'No se pudieron guardar los usuarios', 'error');
      }
    );
  }

  // Métodos para vehículos
  mostrarVehiculos(): void {
    this.cargarVehiculosDisponibles();
    this.mostraFormularioVehiculos = true;
  }

  cerrarFormularioVehiculos(): void {
    this.mostraFormularioVehiculos = false;
    this.searchVehiculos = '';
    this.vehiculosFiltrados = [];
  }

  cargarVehiculosDisponibles(): void {
    const grupoId = this.grupoSeleccionado?.cod_grupo_cliente || 0;

    this.gruposService.getVehiculosDisponibles(grupoId).subscribe(
      (response: any) => {
        this.vehiculosCompletos = (response.data || []).map((v: any) => ({
          ...v,
          checked: this.grupoSeleccionado?.vehiculos?.some(vv => vv.cod_equipo_rastreo === v.cod_equipo_rastreo) || false
        }));
        this.vehiculosFiltrados = [...this.vehiculosCompletos];
      },
      (error) => {
        console.error('Error cargando vehículos:', error);
        Swal.fire('Error', 'No se pudieron cargar los vehículos', 'error');
      }
    );
  }

  filtrarVehiculos(): void {
    if (!this.searchVehiculos.trim()) {
      this.vehiculosFiltrados = [...this.vehiculosCompletos];
    } else {
      const search = this.searchVehiculos.toLowerCase();
      this.vehiculosFiltrados = this.vehiculosCompletos.filter(v =>
        v.des_equipo_ratreo.toLowerCase().includes(search) || v.cod_equipo_rastreo.toString().includes(search)
      );
    }
  }

  guardarVehiculos(): void {
    const vehiculosChecked = this.vehiculosCompletos.filter(v => v.checked);
    const vehiculosSeleccionados = vehiculosChecked.map(v => v.cod_equipo_rastreo);

    // Si el grupo aún no existe, guardar localmente y cerrar
    if (!this.grupoSeleccionado?.cod_grupo_cliente) {
      this.grupoSeleccionado!.vehiculos = vehiculosChecked;
      this.grupoSeleccionado!.vehiculosCount = vehiculosChecked.length;
      Swal.fire('Info', 'Vehículos seleccionados. Se guardarán al crear el grupo.', 'info');
      this.cerrarFormularioVehiculos();
      return;
    }

    this.gruposService.saveVehiculos(this.grupoSeleccionado.cod_grupo_cliente, {
      vehiculos: vehiculosSeleccionados
    }).subscribe(
      (response: any) => {
        Swal.fire('Éxito', 'Vehículos asignados exitosamente', 'success');
        this.grupoSeleccionado!.vehiculos = vehiculosChecked;

        // Actualizar contador
        if (this.grupoSeleccionado) {
          this.grupoSeleccionado.vehiculosCount = vehiculosChecked.length;

          // Actualizar también en el array de grupos para reflejar el cambio en los cards
          const grupoEnLista = this.grupos.find(g => g.cod_grupo_cliente === this.grupoSeleccionado?.cod_grupo_cliente);
          if (grupoEnLista) {
            grupoEnLista.vehiculosCount = vehiculosChecked.length;
          }
        }

        this.cerrarFormularioVehiculos();
      },
      (error) => {
        console.error('Error guardando vehículos:', error);
        Swal.fire('Error', 'No se pudieron guardar los vehículos', 'error');
      }
    );
  }

  // Métodos para eliminar usuarios y vehículos
  eliminarUsuario(indice: number): void {
    if (!this.grupoSeleccionado?.usuarios || !this.grupoSeleccionado?.cod_grupo_cliente) return;

    const usuarioAEliminar = this.grupoSeleccionado.usuarios[indice];

    // Llamar a la API para eliminar el usuario del grupo
    this.gruposService.deleteUsuarioFromGrupo(this.grupoSeleccionado.cod_grupo_cliente, usuarioAEliminar.usuario)
      .subscribe(
        (response: any) => {
          // Remover localmente
          this.grupoSeleccionado!.usuarios!.splice(indice, 1);

          // Actualizar el contador
          if (this.grupoSeleccionado?.usuariosCount !== undefined) {
            this.grupoSeleccionado.usuariosCount--;
          }

          // Actualizar el array de usuariosCompletos para que vuelva a aparecer como no seleccionado
          const usuarioEnLista = this.usuariosCompletos.find(u => u.usuario === usuarioAEliminar.usuario);
          if (usuarioEnLista) {
            usuarioEnLista.checked = false;
          }

          // Actualizar también en el array de grupos para reflejar el cambio en los cards
          const grupoEnLista = this.grupos.find(g => g.cod_grupo_cliente === this.grupoSeleccionado?.cod_grupo_cliente);
          if (grupoEnLista && grupoEnLista.usuariosCount !== undefined) {
            grupoEnLista.usuariosCount--;
          }

          Swal.fire('Éxito', 'Usuario removido del grupo', 'success');
        },
        (error) => {
          console.error('Error eliminando usuario:', error);
          Swal.fire('Error', 'No se pudo eliminar el usuario del grupo', 'error');
        }
      );
  }

  eliminarVehiculo(indice: number): void {
    if (!this.grupoSeleccionado?.vehiculos || !this.grupoSeleccionado?.cod_grupo_cliente) return;

    const vehiculoAEliminar = this.grupoSeleccionado.vehiculos[indice];

    // Llamar a la API para eliminar el vehículo del grupo
    this.gruposService.deleteVehiculoFromGrupo(this.grupoSeleccionado.cod_grupo_cliente, vehiculoAEliminar.cod_equipo_rastreo)
      .subscribe(
        (response: any) => {
          // Remover localmente
          this.grupoSeleccionado!.vehiculos!.splice(indice, 1);

          // Actualizar el contador
          if (this.grupoSeleccionado?.vehiculosCount !== undefined) {
            this.grupoSeleccionado.vehiculosCount--;
          }

          // Actualizar el array de vehiculosCompletos para que vuelva a aparecer como no seleccionado
          const vehiculoEnLista = this.vehiculosCompletos.find(v => v.cod_equipo_rastreo === vehiculoAEliminar.cod_equipo_rastreo);
          if (vehiculoEnLista) {
            vehiculoEnLista.checked = false;
          }

          // Actualizar también en el array de grupos para reflejar el cambio en los cards
          const grupoEnLista = this.grupos.find(g => g.cod_grupo_cliente === this.grupoSeleccionado?.cod_grupo_cliente);
          if (grupoEnLista && grupoEnLista.vehiculosCount !== undefined) {
            grupoEnLista.vehiculosCount--;
          }

          Swal.fire('Éxito', 'Vehículo removido del grupo', 'success');
        },
        (error) => {
          console.error('Error eliminando vehículo:', error);
          Swal.fire('Error', 'No se pudo eliminar el vehículo del grupo', 'error');
        }
      );
  }
}
